import { HELP_CATEGORY_SECTIONS } from './help/help-sections'
import { ModuleTypeEnum } from './types/module-type'
import { ONBOARDING_FLOW_STEPS } from './types/onboarding-steps'
import { SUBSCRIPTION_PLAN_CONFIGS } from './types/subscription-plans'

/** Public product dimensions. Never use record names or arbitrary route slugs. */
export const PRODUCT_MODULES = [
  'about',
  'account',
  'client_portal',
  'organizations',
  'tray_tasks',
  'whats_new',
  'home',
  'other',
  'admin',
  'analytics',
  'calendar',
  'collections',
  'communications',
  'contacts',
  'contracts',
  'deals',
  'documents',
  'entities',
  'events',
  'expenses',
  'financials',
  'focus',
  'forms',
  'getting_started',
  'help',
  'inbox',
  'inventory',
  'invoices',
  'knowledge_base',
  'contacts',
  'live_chat',
  'marketing',
  'meals',
  'meetings',
  'my_notes',
  'my_organization',
  'my_tasks',
  'my_time',
  'notes',
  'notifications',
  'org_chart',
  'products',
  'projects',
  'quotes',
  'reports',
  'scheduling',
  'search',
  'settings',
  'subscription',
  'subscription_setup',
  'suppliers',
  'tasks',
  'team',
  'threads',
  'tickets',
  'time',
  'time_off',
  'timesheets',
  'today',
  'user_settings',
  'workflows',
] as const
export const PRODUCT_FEATURES = [
  'bulk_task_status',
  'project_template',
  'time_timer',
  'data_import',
  'benny_action_plan',
  'client_portal_invite',
] as const
export const ACTIVATION_MILESTONES = [
  'client_added',
  'project_created',
  'task_created',
  'teammate_invited',
  'time_logged',
  'invoice_sent',
] as const
export type ProductSearchLengthBucket = 'empty' | 'short' | 'medium' | 'long'
export type ActivationMilestone = (typeof ACTIVATION_MILESTONES)[number]
export type TrackedFeature = (typeof PRODUCT_FEATURES)[number]
export type ProductEventProperties = Record<
  string,
  string | number | boolean | null | undefined
>

export type ProductEventData = Record<string, string | number | boolean>

type Rule = readonly string[] | 'boolean' | 'count' | 'uuid' | 'help_id'
const modules = [...Object.values(ModuleTypeEnum), ...PRODUCT_MODULES, 'none']
const helpModules = [
  ...new Set(HELP_CATEGORY_SECTIONS.flatMap(section => section.slugs)),
  'inventory',
]
const source = ['blank', 'template', 'manual', 'timer', 'single', 'bulk']
const counts = {
  created: 'count',
  updated: 'count',
  skipped: 'count',
  errors: 'count',
} as const
const task = {
  module: modules,
  count: 'count',
  is_subtask: 'boolean',
  is_recurring: 'boolean',
  source,
} as const
const searchBuckets = ['empty', 'short', 'medium', 'long'] as const
const simpleSources = [
  'other',
  ...PRODUCT_MODULES.map(module => `page:${module}`),
  ...[
    'entity',
    'project',
    'task',
    'contact',
    'deal',
    'contact',
    'member',
    'thread',
    'note',
    'invoice',
    'ticket',
    'event',
    'expense',
    'contract',
    'quote',
    'supplier',
    'document',
  ].map(module => `peek:${module}`),
]

/** Every custom event has its own bounded output shape at client AND storage. */
const rules = {
  session_started: {},
  feature_used: {
    feature: PRODUCT_FEATURES,
    action: ['start', 'stop'],
    module: modules,
    path: ['job', 'direct'],
    success: 'boolean',
    count: 'count',
    step_count: 'count',
    ...counts,
  },
  module_opened: { module: PRODUCT_MODULES },
  task_created: { ...task, priority: ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] },
  task_completed: task,
  project_created: {
    source,
    has_client: 'boolean',
    project_type: ['BASIC', 'KANBAN', 'SCRUM', 'WATERFALL'],
  },
  benny_message_sent: {
    is_new_session: 'boolean',
    has_attachments: 'boolean',
    attachment_count: 'count',
    has_page_context: 'boolean',
    message_length_bucket: ['short', 'medium', 'long'],
  },
  simple_escape: { source: simpleSources },
  export_generated: {
    module: modules,
    format: ['CSV', 'JSON', 'EXCEL'],
    row_count: 'count',
    column_count: 'count',
    scope: ['all', 'filtered'],
    include_custom_fields: 'boolean',
  },
  invite_sent: { count: 'count', failed_count: 'count' },
  help_article_viewed: {
    help_id: 'help_id',
    module_slug: helpModules,
    type: ['workflow', 'concept', 'reference'],
    source: ['article', 'public_help_site'],
  },
  help_search_performed: {
    query_length_bucket: searchBuckets,
    module_slug: helpModules,
    result_count: 'count',
    no_results: 'boolean',
    source: ['help_center', 'public_help_site'],
  },
  help_feedback_submitted: {
    help_id: 'help_id',
    module_slug: helpModules,
    helpful: 'boolean',
    source: ['article', 'public_help_site'],
  },
  scenario_created: { scenario_id: 'uuid' },
  scenario_calculated: {
    scenario_id: 'uuid',
    has_result: 'boolean',
    assumption_types_used: 'count',
    count_new_hires: 'count',
    count_departures: 'count',
    count_rate_changes: 'count',
    count_allocation_changes: 'count',
    count_compensation_changes: 'count',
    count_benefit_changes: 'count',
    count_tax_rate_changes: 'count',
    count_cost_assumptions: 'count',
  },
  scenario_comparison_opened: { scenario_count: 'count' },
  scenario_results_viewed: { scenario_id: 'uuid', has_result: 'boolean' },
  onboarding_flow_started: {
    plan_key: SUBSCRIPTION_PLAN_CONFIGS.map(plan => plan.key),
    step_count: 'count',
  },
  onboarding_step_completed: {
    step_id: ONBOARDING_FLOW_STEPS.map(step => step.id),
  },
  onboarding_step_skipped: {
    step_id: ONBOARDING_FLOW_STEPS.map(step => step.id),
  },
  onboarding_import_completed: { module_type: modules, ...counts },
  onboarding_flow_completed: { completed_steps: 'count', step_count: 'count' },
  activation_milestone: {
    milestone: ACTIVATION_MILESTONES,
    module: modules,
    source,
    count: 'count',
    has_entity_type: 'boolean',
  },
  // Timing only: see apps/web/src/lib/perf/responsiveness.ts.
  responsiveness_measured: {
    metric: ['route_content', 'interaction', 'realtime_recovery'],
    module: modules,
    kind: ['boot', 'navigate'],
    duration_ms: 'count',
    instant: 'boolean',
    refetched: 'count',
  },
} as const satisfies Record<string, Record<string, Rule>>
export type ProductEventName = keyof typeof rules

const UUID = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i
/** Data properties only: telemetry must not execute input getters/coercion. */
function read(value: unknown, key: string): unknown {
  if (!value || typeof value !== 'object') return undefined
  try {
    const descriptor = Object.getOwnPropertyDescriptor(value, key)
    return descriptor && 'value' in descriptor ? descriptor.value : undefined
  } catch {
    return undefined
  }
}
function project(
  rule: Rule,
  value: unknown,
): string | number | boolean | undefined {
  if (Array.isArray(rule))
    return typeof value === 'string' && rule.includes(value) ? value : undefined
  if (rule === 'boolean') return typeof value === 'boolean' ? value : undefined
  if (rule === 'count')
    return typeof value === 'number' &&
      Number.isSafeInteger(value) &&
      value >= 0 &&
      value <= 1_000_000_000
      ? value
      : undefined
  if (typeof value !== 'string' || value.length > 160) return undefined
  if (UUID.test(value)) return value
  if (
    rule === 'help_id' &&
    /^[a-z0-9-]+\.[a-z0-9.-]+$/.test(value) &&
    helpModules.includes(value.split('.')[0]!)
  )
    return value
  return undefined
}
export function productSearchLengthBucket(
  value: unknown,
): (typeof searchBuckets)[number] {
  if (typeof value !== 'string') return 'empty'
  const length = value.trim().length
  return length === 0
    ? 'empty'
    : length <= 40
      ? 'short'
      : length <= 200
        ? 'medium'
        : 'long'
}
export function projectProductEvent(
  event: unknown,
  payload: unknown,
  discriminant?: Partial<Record<'feature' | 'milestone', string>>,
): { event: ProductEventName; properties: ProductEventData } | null {
  if (!isProductEventName(event)) return null
  const name = event as ProductEventName
  const properties: ProductEventData = {}
  for (const [key, rule] of Object.entries(rules[name])) {
    const override = read(discriminant, key)
    const input = override === undefined ? read(payload, key) : override
    const value = project(rule, input)
    if (value !== undefined) properties[key] = value
  }
  // Protect old clients too; only a bucket derived from query text is retained.
  if (name === 'help_search_performed') {
    const query = read(payload, 'query')
    if (typeof query === 'string')
      properties.query_length_bucket = productSearchLengthBucket(query)
  }
  const required: Partial<Record<ProductEventName, string>> = {
    feature_used: 'feature',
    activation_milestone: 'milestone',
    module_opened: 'module',
    onboarding_step_completed: 'step_id',
    onboarding_step_skipped: 'step_id',
    responsiveness_measured: 'metric',
  }
  const key = required[name]
  if (key && properties[key] === undefined) return null
  return { event: name, properties }
}

export function isProductEventName(value: unknown): value is ProductEventName {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(rules, value)
  )
}
